/**
 * Column matching engine — the heart of M3. Given profiled columns it decides
 * the geocoding MODE (coordinate vs address) and assigns each geocoding role to
 * the best column, blending header-name evidence (synonym dictionary + fuzzy
 * distance) with value-pattern evidence via the confidence scorer. Value
 * evidence can and must override a misleading header (criterion 4), and a
 * lat/lng pair is only accepted as coordinate mode with real coordinate
 * evidence, never a stray numeric column (criterion 3).
 *
 * Pure and deterministic: no I/O, no geocoder call. Output is a ColumnMapping
 * the UI pre-fills and the user can override.
 */

import { distance } from "fastest-levenshtein";
import type {
  ColumnMapping,
  ColumnProfile,
  ColumnRole,
  GeocodeMode,
  MatchConfidence,
} from "./types";
import { isCountryLike, isLatValue, isLngValue, isStateCode, isZip, matchRatio } from "./recognizers";
import { scoreRole, type RoleScore } from "./scorer";
import { DICTIONARY, exactRole, normalizeHeader, type MatchableRole } from "./synonyms";

/** Below this combined score a role is left unassigned rather than guessed. */
export const ASSIGN_FLOOR = 0.5;
/** Coordinate columns need at least this combined score to form a pair. */
export const COORD_FLOOR = 0.6;
/**
 * Minimum Levenshtein similarity for a fuzzy header match to count at all.
 * Short-string edit distance is noisy ("Company"≈"Country", "Store Name"≈
 * "Street Name" both score ~0.6-0.7), so only near-exact typos ("Adress"≈
 * "Address", 0.86) clear this bar. Exact aliases and token containment bypass it.
 */
export const FUZZY_MIN = 0.8;

const tokens = (s: string): string[] => (s === "" ? [] : s.split(" "));

/**
 * Header-name match strength for one role, 0..1. Exact synonym alias = 1;
 * otherwise the best of (whole-phrase token containment) and (normalized
 * Levenshtein similarity) across the role's phrases. Token containment lets
 * "Shipping Address" match the `address` phrase; Levenshtein catches typos
 * like "Adress".
 */
export function headerScoreForRole(header: string, role: MatchableRole): number {
  const h = normalizeHeader(header);
  if (h === "") return 0;
  const hTokens = tokens(h);
  let best = 0;
  for (const phrase of DICTIONARY[role]) {
    if (h === phrase) return 1;
    const pTokens = tokens(phrase);
    const contained = pTokens.every((t) => hTokens.includes(t)) ? 1 : 0;
    const lev = 1 - distance(h, phrase) / Math.max(h.length, phrase.length);
    // Only trust Levenshtein for near-exact typos; below FUZZY_MIN it's noise.
    best = Math.max(best, contained, lev >= FUZZY_MIN ? lev : 0);
  }
  return best;
}

/** Value-evidence fraction for the value-recognizable roles; null otherwise. */
function valueScoreForRole(values: string[], role: MatchableRole): number | null {
  switch (role) {
    case "state":
      return matchRatio(values, isStateCode);
    case "zip":
      return matchRatio(values, isZip);
    case "lat":
      return matchRatio(values, isLatValue);
    case "lng":
      return matchRatio(values, isLngValue);
    case "country":
      return matchRatio(values, isCountryLike);
    default:
      return null;
  }
}

interface Candidate {
  col: number;
  role: MatchableRole;
  scored: RoleScore;
}

/** Column-major values from row-major data rows, header-driven width. */
function toColumns(colCount: number, dataRows: string[][]): string[][] {
  return Array.from({ length: colCount }, (_, i) => dataRows.map((r) => r[i] ?? ""));
}

/**
 * Detect a coordinate pair. A column qualifies as lat/lng only with real
 * coordinate evidence: its inferred type is `coordinate` (decimal degrees) OR
 * its header is an exact lat/lng alias (Y/X, latitude/longitude). This is what
 * stops an integer ID column of small numbers from being read as a coordinate.
 */
function detectCoordinatePair(
  profiles: ColumnProfile[],
  columns: string[][],
): { lat: number; lng: number; conf: MatchConfidence } | null {
  const qualifies = (role: "lat" | "lng", p: ColumnProfile): boolean =>
    p.inferred_type === "coordinate" || exactRole(p.header) === role;

  const rank = (role: "lat" | "lng") =>
    profiles
      .filter((p) => qualifies(role, p))
      .map((p) => ({ col: p.index, scored: scoreRole({ headerScore: headerScoreForRole(p.header, role), valueScore: valueScoreForRole(columns[p.index]!, role) }) }))
      .filter((c) => c.scored.score >= COORD_FLOOR)
      .sort((a, b) => b.scored.score - a.scored.score);

  const lats = rank("lat");
  const lngs = rank("lng");
  if (lats.length === 0 || lngs.length === 0) return null;

  // Best distinct pair (a single column can't be both lat and lng).
  let lat = lats[0]!;
  let lng = lngs[0]!;
  if (lat.col === lng.col) {
    if (lats.length > 1 && lngs.length > 1) {
      // pick the assignment that maximizes total score
      const optA = lats[0]!.scored.score + (lngs[1]?.scored.score ?? 0);
      const optB = (lats[1]?.scored.score ?? 0) + lngs[0]!.scored.score;
      if (optA >= optB) lng = lngs[1]!;
      else lat = lats[1]!;
    } else if (lngs.length > 1) {
      lng = lngs[1]!;
    } else if (lats.length > 1) {
      lat = lats[1]!;
    } else {
      return null; // only one coordinate column — not a pair
    }
  }
  const conf: MatchConfidence =
    lat.scored.confidence === "high" && lng.scored.confidence === "high" ? "high" : "medium";
  return { lat: lat.col, lng: lng.col, conf };
}

/**
 * Assemble the full column mapping. Coordinate mode wins when a real lat/lng
 * pair is present; otherwise address mode, preferring a single full-address
 * column and falling back to assembled components.
 */
export function matchColumns(profiles: ColumnProfile[], dataRows: string[][]): ColumnMapping {
  const columns = toColumns(profiles.length, dataRows);

  const roles: Partial<Record<ColumnRole, number>> = {};
  const confidence: Partial<Record<ColumnRole, MatchConfidence>> = {};

  const coord = detectCoordinatePair(profiles, columns);
  let mode: GeocodeMode;

  if (coord) {
    mode = "coordinate";
    roles.lat = coord.lat;
    roles.lng = coord.lng;
    confidence.lat = coord.conf;
    confidence.lng = coord.conf;
  } else {
    mode = "address";
    // Address roles to assign, greedily by score. `label` is handled separately.
    const addressRoles: MatchableRole[] = [
      "address_full", "street", "city", "state", "zip", "country",
    ];
    const candidates: Candidate[] = [];
    for (const p of profiles) {
      for (const role of addressRoles) {
        const scored = scoreRole({
          headerScore: headerScoreForRole(p.header, role),
          valueScore: valueScoreForRole(columns[p.index]!, role),
        });
        if (scored.score >= ASSIGN_FLOOR) candidates.push({ col: p.index, role, scored });
      }
    }
    candidates.sort((a, b) => b.scored.score - a.scored.score);

    // Greedy by descending score; a column goes to its single best role, and a
    // role is filled once. address_full and components coexist happily — an
    // "Address" (street line) plus City/State/Zip columns all get assembled
    // downstream in country-aware order.
    const usedCols = new Set<number>();
    for (const c of candidates) {
      if (roles[c.role] !== undefined || usedCols.has(c.col)) continue;
      roles[c.role] = c.col;
      confidence[c.role] = c.scored.confidence;
      usedCols.add(c.col);
    }
  }

  const label_columns = pickLabelColumns(profiles, roles);

  return { mode, roles, label_columns, confidence };
}

/**
 * Default label/tooltip columns: a name-like column (best `label` match) plus
 * the address column, so markers get a sensible default popup. Editable in UI.
 */
function pickLabelColumns(
  profiles: ColumnProfile[],
  roles: Partial<Record<ColumnRole, number>>,
): number[] {
  const used = new Set(Object.values(roles));
  const labelRanked = profiles
    .map((p) => ({ col: p.index, score: headerScoreForRole(p.header, "label") }))
    .filter((c) => c.score >= ASSIGN_FLOOR)
    .sort((a, b) => b.score - a.score);

  const out: number[] = [];
  const best = labelRanked[0];
  if (best) out.push(best.col);
  else {
    // No name-like header: fall back to the first text column not used for geocoding.
    const fallback = profiles.find((p) => !used.has(p.index) && p.inferred_type === "text");
    if (fallback) out.push(fallback.index);
  }
  if (roles.address_full !== undefined && !out.includes(roles.address_full)) {
    out.push(roles.address_full);
  }
  return out;
}
